import React from 'react';
import { osc, orbitAngle, wobble, lerp, loop, clamp, pulse, stagger } from '../../core/AnimationUtils';

/*
 * Concept-acting LIFE SCENES (Anivaze v2) — biology concepts acted out
 * like a friendly cartoon so a 5-year-old instantly gets it. Pure function
 * of `t`; no Date.now(), Math.random(), React state, or wall-clock CSS.
 * See ANIMATION-PATTERN.md for the full contract.
 */

/* ━━━ Shared prop interface ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface LifeSceneProps {
  opacity?: number;
  accentColor?: string;
  /** Short friendly caption shown at the bottom. */
  label?: string;
  /** Seconds since the scene started. */
  t?: number;
}

/* ━━━ Photosynthesis: plant breathes, sun sends photons, water rises ━━━━━━━ */
export const PhotosynthesisScene: React.FC<LifeSceneProps> = ({
  opacity = 1, accentColor = '#22c55e', t = 0,
  label = 'Plants make food from sunlight!',
}) => {
  /* ── timing ─────────────────────────────────────────────────────────────── */
  const PERIOD = 6;            // full scene loop (s)
  const p = loop(t, PERIOD);   // 0→1 cycle fraction

  /* ── plant breathing / gentle sway ──────────────────────────────────────── */
  const stemSway   = wobble(t, 3.2, 4);          // stem leans slightly
  const plantScale = 1 + osc(t, 2.8, 0, 0.03);  // tiny "breath" scale

  /* ── sun rays ────────────────────────────────────────────────────────────── */
  const sunPulse = osc(t, 2, 0, 1);
  const rayRot   = orbitAngle(t, 8) * 180 / Math.PI;

  /* ── photon particles (3 beams, each staggered) ─────────────────────────── */
  const photons = [0, 1, 2].map(i => {
    const ph    = (loop(t + stagger(i, 1.4), PERIOD));   // phase offset per photon
    // travel from sun (720,70) down-left to top leaf (400,200)
    const px    = lerp(700, 420, ph);
    const py    = lerp(70,  200, ph);
    const palpha = clamp(Math.sin(ph * Math.PI), 0, 1);  // fade in & out
    return { px, py, palpha };
  });

  /* ── water droplets rising through stem (4 drops) ───────────────────────── */
  const drops = [0, 1, 2, 3].map(i => {
    const ph    = loop(t + stagger(i, 0.9), PERIOD * 0.7);
    // rise from roots (400,390) to leaf base (400,230)
    const dy    = lerp(390, 230, ph);
    const dalpha = clamp(Math.sin(ph * Math.PI), 0, 1);
    return { dy, dalpha };
  });

  /* ── CO2 dots drifting in from left ─────────────────────────────────────── */
  const co2dots = [0, 1, 2].map(i => {
    const ph  = loop(t + stagger(i, 1.2), PERIOD);
    const cx2 = lerp(50, 320, ph);
    const cy2 = 200 + i * 18;
    const ca  = clamp(Math.sin(ph * Math.PI), 0, 1);
    return { cx2, cy2, ca };
  });

  /* ── oxygen bubbles drifting out + up from leaf ─────────────────────────── */
  const o2bubbles = [0, 1, 2, 3].map(i => {
    const ph  = loop(t + stagger(i, 0.8), PERIOD * 0.9);
    const bx  = 450 + i * 22 + wobble(t, 1.6, 8, stagger(i, 1.1));
    const by  = lerp(200, 60, ph);
    const ba  = clamp(Math.sin(ph * Math.PI), 0, 1);
    const br  = 7 + i * 2;
    return { bx, by, ba, br };
  });

  /* ── soil ground line ────────────────────────────────────────────────────── */
  const groundY = 370;

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="phs-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#bae6fd" />
          <stop offset="100%" stopColor="#e0f7fa" />
        </linearGradient>
        <radialGradient id="phs-sun" cx="50%" cy="50%">
          <stop offset="0%"   stopColor="#fff9c4" />
          <stop offset="60%"  stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
        <linearGradient id="phs-soil" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#a16207" />
          <stop offset="100%" stopColor="#78350f" />
        </linearGradient>
        <linearGradient id="phs-stem" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#4ade80" />
          <stop offset="100%" stopColor={accentColor} />
        </linearGradient>
      </defs>

      {/* ── Background sky ── */}
      <rect x={0} y={0} width={800} height={450} fill="url(#phs-sky)" />

      {/* ── Soil ── */}
      <rect x={0} y={groundY} width={800} height={450 - groundY} fill="url(#phs-soil)" rx={0} />
      {/* soil texture lines */}
      {[20, 60, 130, 200, 290, 380, 500, 620, 720].map((sx, i) => (
        <line key={i} x1={sx} y1={groundY + 12} x2={sx + 18} y2={groundY + 24}
          stroke="#92400e" strokeWidth={2} opacity={0.5} strokeLinecap="round" />
      ))}

      {/* ── Roots ── */}
      <g transform={`translate(400 ${groundY})`}>
        {[[-28, 40], [0, 50], [28, 40]].map(([rx2, ry2], i) => (
          <path key={i} d={`M0,0 Q${rx2 * 0.5},${ry2 * 0.5} ${rx2},${ry2}`}
            fill="none" stroke="#86efac" strokeWidth={5} strokeLinecap="round" opacity={0.85} />
        ))}
      </g>

      {/* ── Stem (sways gently) ── */}
      <g transform={`translate(400 0)`}>
        <path
          d={`M0,${groundY} Q${stemSway},${(groundY + 240) / 2} ${stemSway * 0.4},240`}
          fill="none" stroke="url(#phs-stem)" strokeWidth={12}
          strokeLinecap="round"
        />
      </g>

      {/* ── Plant (scaled by breathing) ── */}
      <g transform={`translate(400 220) scale(${plantScale})`}>
        {/* Left leaf */}
        <path d={`M${stemSway * 0.3 - 10},0 Q-80,-50 -50,-90 Q-10,-50 ${stemSway * 0.3 - 10},0`}
          fill="#4ade80" stroke="#16a34a" strokeWidth={2} opacity={0.92} />
        {/* Right leaf */}
        <path d={`M${stemSway * 0.3 + 10},0 Q90,-45 55,-85 Q15,-48 ${stemSway * 0.3 + 10},0`}
          fill="#4ade80" stroke="#16a34a" strokeWidth={2} opacity={0.92} />
        {/* Top leaf (bigger, main) */}
        <path d={`M${stemSway * 0.3},0 Q${stemSway * 0.3}-55 ${stemSway * 0.3},-100 Q${stemSway * 0.3 + 40},-55 ${stemSway * 0.3},0`}
          fill="#22c55e" stroke="#15803d" strokeWidth={2} opacity={0.97} />

        {/* Cute face on the main leaf */}
        <circle cx={stemSway * 0.3 - 8} cy={-62} r={3.5} fill="#15803d" />
        <circle cx={stemSway * 0.3 + 8} cy={-62} r={3.5} fill="#15803d" />
        <path d={`M${stemSway * 0.3 - 6},-54 Q${stemSway * 0.3},-50 ${stemSway * 0.3 + 6},-54`}
          fill="none" stroke="#15803d" strokeWidth={2.5} strokeLinecap="round" />
      </g>

      {/* ── Sun ── */}
      <g transform="translate(700 70)">
        <g transform={`rotate(${rayRot})`} opacity={0.88}>
          {Array.from({ length: 12 }, (_, k) => {
            const a  = (k / 12) * Math.PI * 2;
            const r1 = 40 + sunPulse * 3;
            const r2 = 58 + sunPulse * 6;
            return (
              <line key={k}
                x1={r1 * Math.cos(a)} y1={r1 * Math.sin(a)}
                x2={r2 * Math.cos(a)} y2={r2 * Math.sin(a)}
                stroke="#fcd34d" strokeWidth={3} strokeLinecap="round"
              />
            );
          })}
        </g>
        <circle r={32 + sunPulse * 2} fill="url(#phs-sun)" />
        {/* sun smile */}
        <circle cx={-9} cy={-6} r={4} fill="#f59e0b" />
        <circle cx={9}  cy={-6} r={4} fill="#f59e0b" />
        <path d="M-9,6 Q0,13 9,6" fill="none" stroke="#d97706" strokeWidth={2.5} strokeLinecap="round" />
        <text x={0} y={55} textAnchor="middle" fill="#b45309" fontSize={11} fontWeight={700} fontFamily="Inter,sans-serif">light</text>
      </g>

      {/* ── Photon particles (yellow dots travelling sun → leaf) ── */}
      {photons.map(({ px, py, palpha }, i) => (
        <g key={i} opacity={palpha}>
          <circle cx={px} cy={py} r={6} fill="#fde68a" stroke="#fbbf24" strokeWidth={1.5} />
          <circle cx={px - 2} cy={py - 2} r={2} fill="#fff" opacity={0.7} />
        </g>
      ))}

      {/* ── Water droplets (blue, rising through stem) ── */}
      {drops.map(({ dy, dalpha }, i) => (
        <g key={i} opacity={dalpha}>
          <ellipse cx={400 + wobble(t, 1.4, 3, stagger(i, 0.7))} cy={dy} rx={5} ry={7}
            fill="#60a5fa" stroke="#3b82f6" strokeWidth={1.5} />
        </g>
      ))}

      {/* ── CO2 dots drifting in from left ── */}
      {co2dots.map(({ cx2, cy2, ca }, i) => (
        <g key={i} opacity={ca * 0.8}>
          <circle cx={cx2} cy={cy2} r={8} fill="#d1d5db" stroke="#9ca3af" strokeWidth={1.5} />
          <text x={cx2} y={cy2 + 4} textAnchor="middle" fill="#374151" fontSize={7} fontWeight={700} fontFamily="Inter,sans-serif">CO₂</text>
        </g>
      ))}

      {/* ── Oxygen bubbles floating up & out ── */}
      {o2bubbles.map(({ bx, by, ba, br }, i) => (
        <g key={i} opacity={ba * 0.9}>
          <circle cx={bx} cy={by} r={br} fill="none" stroke="#34d399" strokeWidth={2} />
          <circle cx={bx + br * 0.3} cy={by - br * 0.3} r={br * 0.28} fill="white" opacity={0.5} />
        </g>
      ))}
      {/* Label near bubbles */}
      <text x={500} y={42} textAnchor="middle" fill="#059669" fontSize={11} fontWeight={700} fontFamily="Inter,sans-serif" opacity={0.8}>oxygen</text>

      {/* ── CO2 label ── */}
      <text x={90} y={185} textAnchor="middle" fill="#6b7280" fontSize={11} fontWeight={700} fontFamily="Inter,sans-serif" opacity={0.75}>CO₂ in</text>

      {/* ── Water label ── */}
      <text x={430} y={410} textAnchor="middle" fill="#1d4ed8" fontSize={11} fontWeight={700} fontFamily="Inter,sans-serif" opacity={0.8}>water</text>

      {/* ── Friendly caption ── */}
      <g transform="translate(400 427)">
        <rect x={-245} y={-22} width={490} height={40} rx={20} fill="rgba(255,255,255,0.88)" stroke="#bbf7d0" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a" fontSize={16} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};

/* ━━━ Food Chain: grass → rabbit → fox, looping energy-flow scene ━━━━━━━━━ */
export const FoodChainScene: React.FC<LifeSceneProps> = ({
  opacity = 1, accentColor = '#f97316', t = 0,
  label = 'Who eats who!',
}) => {
  const PERIOD = 6;
  const p = loop(t, PERIOD);   // 0→1 overall scene phase

  /* ── Scene phases ────────────────────────────────────────────────────────
   *  0.00–0.35 : rabbit hops toward grass, grass shrinks
   *  0.35–0.55 : chomp pop + sparkles on grass
   *  0.55–0.85 : fox trots toward rabbit, hearts/stars pop
   *  0.85–1.00 : reset (fade back)
   * ─────────────────────────────────────────────────────────────────────── */

  // Positions (x-coords): grass=160, rabbit home=400, fox home=640
  const GRASS_X   = 160;
  const RABBIT_X0 = 400;
  const FOX_X0    = 640;

  /* ── Rabbit moves left toward grass in phase 0→0.35, back in 0.85→1 ── */
  const rabbitPhase = clamp(p / 0.35, 0, 1);                   // 0→1 during approach
  const rabbitReset = clamp((p - 0.85) / 0.15, 0, 1);          // 0→1 during reset
  const rabbitX = p < 0.85
    ? lerp(RABBIT_X0, GRASS_X + 70, rabbitPhase)
    : lerp(GRASS_X + 70, RABBIT_X0, rabbitReset);

  /* ── Rabbit hops (bounce Y during approach) ─────────────────────────── */
  const hopFreq  = 4;
  const hopAmt   = p < 0.35 ? Math.abs(Math.sin(p * Math.PI * hopFreq * 1 / 0.35)) * 18 : 0;
  const rabbitY  = 300 - hopAmt;

  /* ── Grass shrinks after rabbit arrives ─────────────────────────────── */
  const chompPhase  = clamp((p - 0.30) / 0.15, 0, 1);
  const grassScale  = p < 0.85 ? lerp(1, 0.6, chompPhase) : lerp(0.6, 1, clamp((p - 0.85) / 0.15, 0, 1));

  /* ── Chomp pop visible 0.35–0.55 ────────────────────────────────────── */
  const chompVisible = p >= 0.33 && p <= 0.58;
  const chompAlpha   = chompVisible ? clamp(Math.sin(clamp((p - 0.33) / 0.25, 0, 1) * Math.PI), 0, 1) : 0;

  /* ── Fox moves toward rabbit in phase 0.55→0.85, resets 0.85→1 ─────── */
  const foxPhase  = clamp((p - 0.55) / 0.30, 0, 1);
  const foxReset2 = clamp((p - 0.85) / 0.15, 0, 1);
  const foxX = p < 0.85
    ? lerp(FOX_X0, RABBIT_X0 + 80, foxPhase)
    : lerp(RABBIT_X0 + 80, FOX_X0, foxReset2);

  /* ── Fox trot bounce ─────────────────────────────────────────────────── */
  const foxHop  = p >= 0.55 && p < 0.85 ? Math.abs(Math.sin(foxPhase * Math.PI * 4)) * 14 : 0;
  const foxY    = 302 - foxHop;

  /* ── Hearts/stars pop visible 0.78–0.90 ─────────────────────────────── */
  const heartVisible = p >= 0.76 && p <= 0.92;
  const heartAlpha   = heartVisible ? clamp(Math.sin(clamp((p - 0.76) / 0.16, 0, 1) * Math.PI), 0, 1) : 0;

  /* ── Arrow pulse (energy flowing along chain) ────────────────────────── */
  const arrowPulse1 = osc(t, 1.1, 0.4, 1);     // grass→rabbit arrow
  const arrowPulse2 = osc(t, 1.1, 0.4, 1, 1);  // rabbit→fox arrow (offset)

  /* ── Ground / sky ────────────────────────────────────────────────────── */
  const groundY = 330;

  /* ── Rabbit facing direction ────────────────────────────────────────── */
  const rabbitFlip = p < 0.85 ? -1 : 1;   // face left while hopping to grass, right while resetting

  /* ── Fox facing direction ───────────────────────────────────────────── */
  const foxFlip = p < 0.85 ? -1 : 1;

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="fc-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#e0f2fe" />
          <stop offset="100%" stopColor="#bbf7d0" />
        </linearGradient>
        <linearGradient id="fc-ground" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#86efac" />
          <stop offset="100%" stopColor="#4ade80" />
        </linearGradient>
      </defs>

      {/* ── Background ── */}
      <rect x={0} y={0}       width={800} height={groundY} fill="url(#fc-sky)" />
      <rect x={0} y={groundY} width={800} height={450 - groundY} fill="url(#fc-ground)" />

      {/* ── Ground decoration (small flowers) ── */}
      {[100, 260, 340, 500, 600, 720].map((fx, i) => (
        <g key={i} transform={`translate(${fx} ${groundY + 10})`}>
          <circle r={4} fill={i % 2 === 0 ? '#fde68a' : '#fb7185'} />
          <circle cx={0} cy={-7} r={3} fill={i % 2 === 0 ? '#fcd34d' : '#f43f5e'} />
        </g>
      ))}

      {/* ── Clouds ── */}
      {[{ ox: 0, y: 60, s: 1 }, { ox: 340, y: 90, s: 0.75 }].map((c, i) => {
        const cx2 = ((t * 9 + c.ox) % 920) - 100;
        return (
          <g key={i} transform={`translate(${cx2} ${c.y}) scale(${c.s})`} opacity={0.9}>
            <ellipse cx={0} cy={0} rx={40} ry={26} fill="#fff" />
            <ellipse cx={40} cy={6}  rx={30} ry={20} fill="#fff" />
            <ellipse cx={-34} cy={8} rx={26} ry={18} fill="#fff" />
          </g>
        );
      })}

      {/* ─── GRASS character ─── */}
      <g transform={`translate(${GRASS_X} ${groundY}) scale(${grassScale})`}>
        {/* grass blades */}
        {[-20, -8, 4, 16, 28].map((gx, i) => (
          <path key={i}
            d={`M${gx},0 Q${gx + wobble(t, 2.2, 5, stagger(i, 0.4))},-${38 + i * 3} ${gx},-${55 + i * 4}`}
            fill="none" stroke="#16a34a" strokeWidth={5 + (i % 2)} strokeLinecap="round"
          />
        ))}
        {/* patch base */}
        <ellipse cx={5} cy={2} rx={40} ry={10} fill="#15803d" opacity={0.4} />
        {/* face on grass patch */}
        <circle cx={-4}  cy={-20} r={3.5} fill="#14532d" />
        <circle cx={12}  cy={-20} r={3.5} fill="#14532d" />
        <path d="M-3,-13 Q4,-10 11,-13" fill="none" stroke="#14532d" strokeWidth={2.5} strokeLinecap="round" />
        {/* label */}
        <text x={5} y={28} textAnchor="middle" fill="#14532d" fontSize={12} fontWeight={700} fontFamily="Inter,sans-serif">Grass</text>
      </g>

      {/* ─── Arrow 1: grass → rabbit ─── */}
      <g opacity={arrowPulse1}>
        <line x1={GRASS_X + 55} y1={groundY - 60} x2={RABBIT_X0 - 65} y2={groundY - 60}
          stroke={accentColor} strokeWidth={4} strokeLinecap="round"
          strokeDasharray="14 8"
          strokeDashoffset={-(loop(t, 1.2) * 22)}
        />
        <polygon points={`${RABBIT_X0 - 60},${groundY - 70} ${RABBIT_X0 - 45},${groundY - 60} ${RABBIT_X0 - 60},${groundY - 50}`}
          fill={accentColor} />
        {/* energy label */}
        <text x={(GRASS_X + 55 + RABBIT_X0 - 65) / 2} y={groundY - 72}
          textAnchor="middle" fill={accentColor} fontSize={11} fontWeight={700} fontFamily="Inter,sans-serif">energy</text>
      </g>

      {/* ─── Arrow 2: rabbit → fox ─── */}
      <g opacity={arrowPulse2}>
        <line x1={RABBIT_X0 + 65} y1={groundY - 60} x2={FOX_X0 - 65} y2={groundY - 60}
          stroke="#f97316" strokeWidth={4} strokeLinecap="round"
          strokeDasharray="14 8"
          strokeDashoffset={-(loop(t, 1.2) * 22)}
        />
        <polygon points={`${FOX_X0 - 60},${groundY - 70} ${FOX_X0 - 45},${groundY - 60} ${FOX_X0 - 60},${groundY - 50}`}
          fill="#f97316" />
        <text x={(RABBIT_X0 + 65 + FOX_X0 - 65) / 2} y={groundY - 72}
          textAnchor="middle" fill="#f97316" fontSize={11} fontWeight={700} fontFamily="Inter,sans-serif">energy</text>
      </g>

      {/* ─── RABBIT ─── */}
      <g transform={`translate(${rabbitX} ${rabbitY}) scale(${rabbitFlip} 1)`}>
        {/* shadow */}
        <ellipse cx={0} cy={40} rx={28} ry={8} fill="rgba(0,0,0,0.12)" />
        {/* body */}
        <ellipse cx={0} cy={18} rx={22} ry={20} fill="#f3f4f6" stroke="#d1d5db" strokeWidth={2} />
        {/* head */}
        <circle cx={0} cy={-8} r={16} fill="#f9fafb" stroke="#d1d5db" strokeWidth={2} />
        {/* ears */}
        <ellipse cx={-9} cy={-28} rx={6} ry={14} fill="#f9fafb" stroke="#d1d5db" strokeWidth={2} />
        <ellipse cx={9}  cy={-28} rx={6} ry={14} fill="#f9fafb" stroke="#d1d5db" strokeWidth={2} />
        <ellipse cx={-9} cy={-28} rx={3.5} ry={10} fill="#fca5a5" />
        <ellipse cx={9}  cy={-28} rx={3.5} ry={10} fill="#fca5a5" />
        {/* face */}
        <circle cx={-5}  cy={-10} r={3} fill="#111827" />
        <circle cx={5}   cy={-10} r={3} fill="#111827" />
        <circle cx={-4}  cy={-11} r={1} fill="#fff" />
        <circle cx={6}   cy={-11} r={1} fill="#fff" />
        <path d="M-4,-2 Q0,3 4,-2" fill="none" stroke="#9ca3af" strokeWidth={2} strokeLinecap="round" />
        {/* tail */}
        <circle cx={-20} cy={22} r={7} fill="#f3f4f6" stroke="#d1d5db" strokeWidth={1.5} />
        {/* legs (bounce animates via rabbitY) */}
        <ellipse cx={-10} cy={36} rx={8} ry={6} fill="#f3f4f6" stroke="#d1d5db" strokeWidth={1.5} />
        <ellipse cx={10}  cy={36} rx={8} ry={6} fill="#f3f4f6" stroke="#d1d5db" strokeWidth={1.5} />
        {/* label */}
        <text x={0} y={56} textAnchor="middle" fill="#374151" fontSize={12} fontWeight={700} fontFamily="Inter,sans-serif" transform="scale(-1 1) translate(0 0)">Rabbit</text>
      </g>

      {/* ─── Chomp pop! ─── */}
      {[0, 1, 2, 3, 4].map(i => {
        const a = (i / 5) * Math.PI * 2;
        const r2 = 24 + i * 4;
        return (
          <g key={i} opacity={chompAlpha}
            transform={`translate(${rabbitX + 30 * rabbitFlip} ${rabbitY - 6})`}>
            <circle cx={r2 * Math.cos(a)} cy={r2 * Math.sin(a)} r={4 + i * 1.5}
              fill={i % 2 === 0 ? '#fde68a' : '#fb7185'} />
          </g>
        );
      })}
      <text x={rabbitX + 30 * rabbitFlip} y={rabbitY - 30}
        textAnchor="middle" fill="#dc2626" fontSize={15} fontWeight={800} fontFamily="Inter,sans-serif"
        opacity={chompAlpha}>nom!</text>

      {/* ─── FOX ─── */}
      <g transform={`translate(${foxX} ${foxY}) scale(${foxFlip} 1)`}>
        {/* shadow */}
        <ellipse cx={0} cy={44} rx={34} ry={8} fill="rgba(0,0,0,0.12)" />
        {/* body */}
        <ellipse cx={0} cy={22} rx={28} ry={22} fill="#fb923c" stroke="#ea580c" strokeWidth={2} />
        {/* white belly */}
        <ellipse cx={0} cy={26} rx={16} ry={14} fill="#fef3c7" />
        {/* head */}
        <circle cx={0} cy={-4} r={20} fill="#fb923c" stroke="#ea580c" strokeWidth={2} />
        {/* ears */}
        <polygon points="-16,-18 -10,-42 -2,-18" fill="#fb923c" stroke="#ea580c" strokeWidth={2} />
        <polygon points="16,-18 10,-42 2,-18"   fill="#fb923c" stroke="#ea580c" strokeWidth={2} />
        <polygon points="-14,-20 -10,-36 -4,-20" fill="#fca5a5" />
        <polygon points="14,-20 10,-36 4,-20"    fill="#fca5a5" />
        {/* snout */}
        <ellipse cx={0} cy={5} rx={9} ry={6} fill="#fef3c7" />
        <ellipse cx={0} cy={4} rx={4} ry={2.5} fill="#111827" />
        {/* eyes */}
        <circle cx={-8}  cy={-8} r={3.5} fill="#111827" />
        <circle cx={8}   cy={-8} r={3.5} fill="#111827" />
        <circle cx={-7}  cy={-9} r={1.2} fill="#fff" />
        <circle cx={9}   cy={-9} r={1.2} fill="#fff" />
        {/* bushy tail */}
        <path d="M28,22 Q58,10 54,38 Q46,46 28,38" fill="#fb923c" stroke="#ea580c" strokeWidth={2} />
        <ellipse cx={52} cy={32} rx={9} ry={7} fill="#fef3c7" />
        {/* legs */}
        <ellipse cx={-12} cy={42} rx={9} ry={6} fill="#fb923c" stroke="#ea580c" strokeWidth={1.5} />
        <ellipse cx={12}  cy={42} rx={9} ry={6} fill="#fb923c" stroke="#ea580c" strokeWidth={1.5} />
        {/* label */}
        <text x={0} y={60} textAnchor="middle" fill="#7c2d12" fontSize={12} fontWeight={700} fontFamily="Inter,sans-serif" transform="scale(-1 1) translate(0 0)">Fox</text>
      </g>

      {/* ─── Hearts / stars pop near fox mouth ─── */}
      {[0, 1, 2].map(i => {
        const a = (i / 3) * Math.PI - Math.PI / 2;
        const r2 = 34 + i * 10;
        return (
          <g key={i} opacity={heartAlpha}
            transform={`translate(${foxX - 40 * foxFlip} ${foxY - 10})`}>
            <text x={r2 * Math.cos(a)} y={r2 * Math.sin(a)}
              textAnchor="middle" fontSize={i === 1 ? 18 : 14}
              fill={i === 1 ? '#f43f5e' : '#fbbf24'}
              fontFamily="Inter,sans-serif">
              {i === 1 ? '♥' : '★'}
            </text>
          </g>
        );
      })}

      {/* ── Friendly caption ── */}
      <g transform="translate(400 427)">
        <rect x={-195} y={-22} width={390} height={40} rx={20} fill="rgba(255,255,255,0.88)" stroke="#fed7aa" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a" fontSize={16} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};
